"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, motion, type PanInfo, useDragControls } from "framer-motion";
import { X } from "lucide-react";
import * as React from "react";

import { easeSmooth, springConfig } from "@/lib/animations";
import { cn } from "@/lib/utils";

const DRAG_DISMISS_THRESHOLD = 80;
const DRAG_VELOCITY_THRESHOLD = 400;

interface SheetContextValue {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}

const SheetContext = React.createContext<SheetContextValue>({ open: false });

function Sheet({
  open = false,
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) {
  return (
    <SheetContext.Provider value={{ open, onOpenChange }}>
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} {...props} />
    </SheetContext.Provider>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(true);
  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    setIsMobile(!mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(!e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

const SheetContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    disableOutsideClick?: boolean;
  }
>(({ className, children, disableOutsideClick = false, ...props }, ref) => {
  const { open, onOpenChange } = React.useContext(SheetContext);
  const isMobile = useIsMobile();
  const dragControls = useDragControls();
  const [overlayOpacity, setOverlayOpacity] = React.useState(1);

  React.useEffect(() => {
    if (open) setOverlayOpacity(1);
  }, [open]);

  const handleDrag = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const y = Math.max(0, info.offset.y);
    setOverlayOpacity(Math.max(0.3, 1 - y / 400));
  };

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (info.offset.y > DRAG_DISMISS_THRESHOLD || info.velocity.y > DRAG_VELOCITY_THRESHOLD) {
      onOpenChange?.(false);
    } else {
      setOverlayOpacity(1);
    }
  };

  return (
    <DialogPrimitive.Portal forceMount>
      <AnimatePresence>
        {open && (
          <>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: overlayOpacity }}
                exit={{ opacity: 0 }}
                transition={easeSmooth}
                className="fixed inset-0 z-50 bg-black/70"
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content
              ref={ref}
              asChild
              forceMount
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                requestAnimationFrame(() => {
                  const content = e.target as HTMLElement;
                  if (!content.contains(document.activeElement)) {
                    content.focus();
                  }
                });
              }}
              onPointerDownOutside={(e) => {
                if (disableOutsideClick) e.preventDefault();
              }}
              {...props}
            >
              <motion.div
                initial={isMobile ? { y: "100%" } : { opacity: 0, scale: 0.95 }}
                animate={isMobile ? { y: 0 } : { opacity: 1, scale: 1 }}
                exit={isMobile ? { y: "100%" } : { opacity: 0, scale: 0.95 }}
                transition={isMobile ? springConfig : easeSmooth}
                drag={isMobile ? "y" : false}
                dragControls={isMobile ? dragControls : undefined}
                dragListener={false}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.6 }}
                onDrag={isMobile ? handleDrag : undefined}
                onDragEnd={isMobile ? handleDragEnd : undefined}
                tabIndex={-1}
                className={cn(
                  "fixed z-50 flex flex-col overflow-hidden bg-background shadow-2xl outline-none",
                  isMobile
                    ? "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl border-t border-border pb-[env(safe-area-inset-bottom)]"
                    : "left-1/2 top-1/2 w-full max-w-md max-h-[85dvh] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border",
                  className
                )}
              >
                {isMobile && (
                  <div
                    className="flex cursor-grab items-center justify-center py-2 active:cursor-grabbing"
                    onPointerDown={(e) => dragControls.start(e)}
                  >
                    <div aria-hidden className="h-1 w-10 rounded-full bg-muted-foreground/30" />
                  </div>
                )}
                {children}
              </motion.div>
            </DialogPrimitive.Content>
          </>
        )}
      </AnimatePresence>
    </DialogPrimitive.Portal>
  );
});
SheetContent.displayName = "SheetContent";

const SheetHeader = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "shrink-0 flex items-start justify-between gap-4 px-3 py-4 sm:px-6 sm:py-6",
      className
    )}
    {...props}
  >
    <div className="flex min-w-0 flex-1 flex-col gap-1">{children}</div>
    <DialogPrimitive.Close className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
      <X className="size-4" />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  </div>
);
SheetHeader.displayName = "SheetHeader";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold tracking-tight", className)}
    {...props}
  />
));
SheetTitle.displayName = "SheetTitle";

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = "SheetDescription";

const SheetSection = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("shrink-0 px-3 sm:px-6 pb-4 sm:pb-6", className)} {...props} />
);
SheetSection.displayName = "SheetSection";

const SheetBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className="flex-1 min-h-0 overflow-y-auto px-3 py-0.5 sm:px-6">
    <div className={cn("flex flex-col gap-6 pb-4 sm:pb-6", className)} {...props} />
  </div>
);
SheetBody.displayName = "SheetBody";

const SheetFooter = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("shrink-0 bg-background px-3 pb-4 sm:pb-6 pt-0 sm:px-6", className)}
    {...props}
  >
    <div className="mb-4 sm:mb-6 border-t border-border" />
    <div className="flex flex-col gap-2">{children}</div>
  </div>
);
SheetFooter.displayName = "SheetFooter";

export {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetSection,
  SheetTitle,
};
